/**
 * Keeping a page that is already open up to date (B914).
 *
 * Mike, 2026-09-21: "when i click on chats, unless I refresh the page it is not updating live, can you
 * apply this fix and message another session to make this a global setting as this is also a problem on
 * the app."
 *
 * WHAT WAS ACTUALLY WRONG, so this is not mistaken for the whole fix. Two faults wear the same symptom:
 *
 *   1. A page left open never asks again. Every surface reads once when it mounts and then sits there.
 *      Moving between pages does remount them, so clicking through the rail does refetch -- but the tab
 *      left open on one page while the person works in Teams, or the desktop app behind another window,
 *      shows whatever was true when they last looked. THIS is what this file fixes.
 *   2. The Chats page's Teams half is a CACHE, rebuilt by a timer. Until that timer ran, no amount of
 *      refetching -- or reloading the browser -- could show a new Teams message, because the store's own
 *      answer was stale. That is fixed in the builder (B915), not here. It is the one Mike hit.
 *
 * THE POLICY, for every page that adopts it:
 *
 *   1. A surface re-reads when the person comes back to it.
 *   2. Optionally on a slow beat while they are looking at it -- and **no beat on a read nobody has
 *      timed**. That rule is the adminui session's, from measuring its own surfaces on 2026-09-21: two of
 *      its three reads walk an 858 MB table under a per-row policy, and a beat there is not a stale page
 *      asking again, it is the store on its knees. The Chats list's own read was timed before it was given
 *      one: 112 to 185 ms for 374 KB on Mike's live store, three runs.
 *   3. **Before a surface takes a beat, look at what its effects are keyed on.** A re-read hands the
 *      page a NEW OBJECT, so every `useEffect(..., [view])` on it fires again -- and a beat turns
 *      "once, when it loaded" into "six times a minute". The Chats thread was one line from shipping
 *      this (B1410): its scroll-to-bottom was keyed on the loaded view, so adopting the policy would
 *      have dragged the reader to the foot every ten seconds, out of whatever history they had
 *      scrolled up to read, with no way to stay there. Nothing fails; the page just becomes unusable.
 *      Key such an effect on what actually changed -- an id, the newest row's id -- and guard it with
 *      the state it would destroy (still near the foot? no draft being typed?). **This bites without a
 *      beat as well**, just less often: a return-triggered re-read is a new object too, so a scroll
 *      reset or a cleared selection fires every time somebody comes back to the window.
 *   4. Never while the page is hidden. A page nobody is looking at must cost nothing, and on a phone a
 *      background poll is somebody's battery.
 *   5. Never two at once, and never for ever. See watchForAttention.
 */
import { useEffect, useRef } from 'react';

/** A slow beat for a list that changes by itself. Fast enough to feel live, slow enough to be free. */
export const LIVE_BEAT_MS = 60_000;

/**
 * How long a re-read is given before it is abandoned. A page that never answers must not leave a request
 * running: the adminui session found two of its reads holding ACCESS SHARE on audit.judgement long enough
 * to fail another session's migration twice in one morning, and that was WITHOUT a trigger attached.
 */
export const LIVE_DEADLINE_MS = 30_000;

/**
 * Coming back to a window is not one event. `focus` and `visibilitychange` both fire, and on some returns
 * `focus` fires more than once, so ONE return asked for three reads of /api/triage on 2026-09-21 -- the
 * heaviest read on the estate, tripled, every time he came back. The in-flight guard below could not stop
 * it, because it can only see a read that hands back a promise and several of these loaders hand back
 * their canceller instead. This floor collapses the burst whatever the loader returns: events closer
 * together than this are one return, and a genuine return minutes later is never affected.
 */
export const LIVE_COALESCE_MS = 500;

/**
 * What a page gives this policy. The signal is the page's to pass to fetch; returning the PROMISE is what
 * lets the policy know the read is still running, and a page that returns anything else -- nothing, or the
 * cleanup function some of these loaders return -- simply gets no protection from stacking. The return is
 * `unknown` on purpose: every existing loader on the portal should be able to adopt this without being
 * rewritten first, and the ones worth protecting can return their promise when somebody gets to them.
 */
export type LiveRefresh = (signal: AbortSignal) => unknown;

/** Just enough of window and document to wire this up, so the rule can be proved without a browser. */
export interface Attention {
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}
export interface LiveEnv {
  win: Attention;
  doc: Attention & { visibilityState: string };
  setInterval(fn: () => void, ms: number): unknown;
  clearInterval(handle: unknown): void;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  /** The clock, so the coalescing floor can be proved without waiting for real time. Defaults to Date.now. */
  now?(): number;
}

/**
 * The rule itself, away from React so it can be tested without a DOM (this package has no jsdom, and a
 * policy every page is going to adopt should be provable rather than assumed).
 *
 * `focus` alone fires for a window that is showing a different tab, and `visibilitychange` alone misses a
 * window raised from behind another application, so it listens to both and asks the same question of each:
 * is somebody actually looking at this? Returns the undo.
 */
export function watchForAttention(refresh: LiveRefresh, everyMs: number, env: LiveEnv, deadlineMs = LIVE_DEADLINE_MS): () => void {
  // NEVER TWO AT ONCE. This is the half of the policy that is easy to leave out and dangerous to: a
  // refetch-on-focus that can stack is the old bug with a trigger attached. Alt-tab four times at a page
  // whose read takes longer than the beat and you have four reads in flight, each holding its own
  // transaction, and the slowest page on the estate is the one that gets hit hardest.
  let inFlight: AbortController | null = null;
  let startedAt = Number.NEGATIVE_INFINITY;
  const clock = () => (env.now ? env.now() : Date.now());
  const whenLookedAt = () => {
    if (env.doc.visibilityState !== 'visible') return;
    if (inFlight) return;
    // One return, one read: see LIVE_COALESCE_MS.
    if (clock() - startedAt < LIVE_COALESCE_MS) return;
    startedAt = clock();
    const attempt = new AbortController();
    inFlight = attempt;
    const deadline = env.setTimeout(() => attempt.abort(), deadlineMs);
    const done = () => { env.clearTimeout(deadline); if (inFlight === attempt) inFlight = null; };
    let answer: unknown;
    try { answer = refresh(attempt.signal); } catch { done(); return; }
    // A page that returns its PROMISE gets the stacking guard and the deadline; one that returns anything
    // else -- nothing, or the cleanup function several of these loaders return -- is released at once,
    // which is no worse than it was before this policy existed. Checked by shape rather than by type,
    // because the loaders on this portal are not all promises and none of them should have to be rewritten
    // before it can have this.
    const thenable = answer as { then?: unknown } | null | undefined;
    if (thenable && typeof thenable.then === 'function') (answer as Promise<unknown>).then(done, done);
    else done();
  };
  env.win.addEventListener('focus', whenLookedAt);
  env.doc.addEventListener('visibilitychange', whenLookedAt);
  // A beat, never a poll: it asks the same question, so a hidden tab costs nothing and a phone in a
  // pocket is not woken up to fetch a list nobody is reading.
  const beat = everyMs > 0 ? env.setInterval(whenLookedAt, everyMs) : null;
  return () => {
    env.win.removeEventListener('focus', whenLookedAt);
    env.doc.removeEventListener('visibilitychange', whenLookedAt);
    if (beat !== null) env.clearInterval(beat);
    // Walking away from a page ends its read. Otherwise the transaction behind it runs on server-side
    // after nobody is waiting for the answer.
    inFlight?.abort();
  };
}

/**
 * Re-read when a socket comes back, and only then (B1470).
 *
 * A pane fed by a WebSocket needs no beat, but **events sent while the socket is down are not replayed**.
 * When it reconnects, whatever was said in the gap is simply not there, and stays missing until the page
 * is reloaded -- which is Mike's own complaint of 2026-09-21 arriving from a second cause.
 *
 * It is not a rare case. **Every deploy restarts the API and drops every socket**, and there were three
 * deploys in one afternoon on 2026-09-21; anyone with a chat open across one of them lost the window.
 *
 * `useMessengerSocket`'s `onState` reports open and closed and does NOT say whether an open is a RE-open,
 * so the state is kept here. THE FIRST OPEN MUST NOT RE-READ: the load that opened the surface has just
 * run, and re-reading on it would double every mount.
 */
export function reReadOnReconnect(reRead: () => void): (open: boolean) => void {
  let wasDown = false;
  return (open: boolean) => {
    if (!open) { wasDown = true; return; }
    if (!wasDown) return;
    wasDown = false;
    reRead();
  };
}

/**
 * Re-read when the person comes back to this page, and on a beat while it is in front of them.
 *
 * `refresh` may be a new function on every render; it is held in a ref, so the listeners are attached
 * once and never churn. `everyMs` of 0 means "only when they come back".
 */
export function useLiveRefresh(refresh: LiveRefresh, everyMs: number = 0): void {
  const latest = useRef(refresh);
  latest.current = refresh;
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    return watchForAttention((signal) => latest.current(signal), everyMs, {
      win: window, doc: document,
      setInterval: (fn, ms) => window.setInterval(fn, ms),
      clearInterval: (h) => window.clearInterval(h as number),
      setTimeout: (fn, ms) => window.setTimeout(fn, ms),
      clearTimeout: (h) => window.clearTimeout(h as number),
    });
  }, [everyMs]);
}
